import { useMemo, useState } from 'react';
import { buildConfig, resolveMode } from './config';
import { KioskFlow } from './flows/KioskFlow';
import { KioskFrame } from './components/KioskFrame';
import { WebFlow } from './flows/WebFlow';
import { AdminScreen } from './screens/AdminScreen';
import { ClipScreen } from './screens/ClipScreen';
import { CodeEntryScreen } from './screens/CodeEntryScreen';
import { normaliseShareCode } from './lib/shareCode';
import { TAPS_KIOSK, useAdminToggle } from './lib/useAdminToggle';
import { recorderSupported } from './capture';
import { noteEvent } from './lib/diagnostics';

/**
 * One codebase, two deployment targets. Mode is resolved once — from ?mode= or
 * the build env — and everything downstream reads the resolved config rather
 * than sniffing the mode again.
 *
 * The admin overlay sits above both flows so it is reachable from any screen,
 * including a broken one.
 */
/**
 * /clip/<session id> — where the SMS link lands. Checked before anything else,
 * because this page is not the activation: no camera, no AR module, no flow.
 * The catch-all redirect in netlify.toml already serves the app shell here.
 */
function clipSessionId(pathname: string = window.location.pathname): string | null {
  const match = /^\/clip\/([A-Za-z0-9-]{1,100})\/?$/.exec(pathname);
  return match?.[1] ?? null;
}

/**
 * /c/<code> — the same retrieval page, reached by a code a kiosk guest copied
 * down instead of leaving a phone number. /c on its own asks for the code.
 */
function clipShareCode(pathname: string = window.location.pathname): string | null {
  const match = /^\/c\/([A-Za-z0-9]{1,16})\/?$/.exec(pathname);
  return match ? normaliseShareCode(match[1] as string) : null;
}

function codeEntryRequested(pathname: string = window.location.pathname): boolean {
  return /^\/c\/?$/.test(pathname);
}

export default function App() {
  const sessionId = useMemo(() => clipSessionId(), []);
  const [shareCode, setShareCode] = useState<string | null>(() => clipShareCode());
  const wantsCodeEntry = useMemo(() => codeEntryRequested(), []);

  const config = useMemo(() => {
    const mode = resolveMode();
    const built = buildConfig(mode);
    noteEvent(`booting in ${mode} mode`);
    if (!recorderSupported()) {
      // Not fatal: the guest still gets the transformation, just no clip. Worth
      // knowing about loudly, because on the kiosk it makes SMS pointless.
      noteEvent('WARNING: MediaRecorder or canvas.captureStream unsupported');
    }
    return built;
  }, []);

  // `config` decides, not the hostname — that is what keeps the diagnostics
  // view reachable on the unit, where the shell serves from
  // appassets.androidplatform.net. Five taps there rather than three.
  const [adminOpen, setAdminOpen] = useAdminToggle(
    config.adminEnabled,
    config.mode === 'kiosk' ? TAPS_KIOSK : undefined,
  );

  if (sessionId || shareCode) {
    return (
      <div className="h-full min-h-[var(--app-h)] w-full bg-ink">
        <ClipScreen
          sessionId={sessionId ?? undefined}
          shareCode={shareCode ?? undefined}
        />
      </div>
    );
  }

  if (wantsCodeEntry) {
    return (
      <div className="h-full min-h-[var(--app-h)] w-full bg-ink">
        <CodeEntryScreen
          onSubmit={(code) => {
            const next = normaliseShareCode(code);
            // Keep the URL shareable without pulling in a router.
            window.history.pushState(null, '', `/c/${next}`);
            setShareCode(next);
          }}
        />
      </div>
    );
  }

  // The kiosk is always laid out at the panel's own size and scaled to fit
  // whatever is previewing it — on the unit that scale comes out at 1. The
  // admin overlay stays outside the frame so it is readable at any window size.
  if (config.mode === 'kiosk') {
    return (
      <>
        <KioskFrame>
          <KioskFlow config={config} />
        </KioskFrame>

        {adminOpen ? (
          <div className="fixed inset-0 z-[100]">
            <AdminScreen config={config} onClose={() => setAdminOpen(false)} />
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div className="h-full min-h-[var(--app-h)] w-full bg-ink">
      <WebFlow config={config} />

      {adminOpen ? (
        <div className="fixed inset-0 z-[100]">
          <AdminScreen config={config} onClose={() => setAdminOpen(false)} />
        </div>
      ) : null}
    </div>
  );
}
